import React, { useState } from "react";
import { Box, Typography, Stepper, Step, StepLabel, Button } from "@mui/material";
import { useLocation } from "react-router-dom";
import Topbar from "../Topbar";
import { CreateProjectData } from "./types";
import CreateProjectStepContent from "./CreateProjectStepContent";
import { CREATE_PROJECT_STEPS as steps, canProceedFrom, newCreateProjectData } from "./wizardSteps";

interface CreateProjectNavState {
  selectedLatLng?: { lat: number; lng: number } | null;
  /** The listing folder the wizard was opened from. */
  folderId?: number | null;
}

const CreateProjectWizard: React.FC = () => {
  // The project listing map can pick a location before sending the user here.
  // It only seeds the Location & Size step — the user can still move the point.
  const { state } = useLocation();
  const presetLatLng = (state as CreateProjectNavState | null)?.selectedLatLng ?? null;
  const presetFolderId = (state as CreateProjectNavState | null)?.folderId ?? null;

  const [projectData, setProjectData] = useState<CreateProjectData>(() =>
    newCreateProjectData({ selectedLatLng: presetLatLng, folderId: presetFolderId })
  );

  const [activeStep, setActiveStep] = useState<number>(0);

  const canProceed = (step: number): boolean => canProceedFrom(step, projectData);

  const handleNext = () => {
    setActiveStep((prevStep: number) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep: number) => prevStep - 1);
  };

  const updateProjectData = (updates: Partial<CreateProjectData>) => {
    setProjectData((prev: CreateProjectData) => ({ ...prev, ...updates }));
  };

  return (
    <Box sx={{ height: "var(--vh100)", display: "flex", flexDirection: "column" }}>
      {/* Topbar Navigation */}
      <Topbar />

      {/* Header */}
      <Box
        sx={{
          backgroundColor: "white",
          p: 3,
          borderBottom: "1px solid #e0e0e0",
          zIndex: 1000,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Create New Project
        </Typography>
        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {steps.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel
                sx={{
                  "& .MuiStepIcon-root": {
                    color: index <= activeStep ? "#00d4ad" : "#e0e0e0",
                    "&.Mui-completed": {
                      color: "#00d4ad",
                    },
                    "&.Mui-active": {
                      color: "#00d4ad",
                    },
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: index <= activeStep ? "#1a1a1a" : "#9e9e9e",
                    fontWeight: index === activeStep ? 600 : 400,
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        <Typography variant="body2" color="text.secondary">
          {`Step ${activeStep + 1} of ${steps.length}`}
        </Typography>
      </Box>

      {/* Step Content */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <CreateProjectStepContent
          step={activeStep}
          data={projectData}
          onNext={handleNext}
          onBack={handleBack}
          onUpdateData={updateProjectData}
        />
      </Box>

      {/* Global Bottom Navigation */}
      {activeStep < steps.length - 1 && (
        <Box
          sx={{
            p: 3,
            backgroundColor: "#f8f9fa",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            bottom: 0,
            zIndex: 1000,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0}
            sx={{
              borderColor: "#e0e0e0",
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                borderColor: "#ccc",
              },
            }}
          >
            Previous
          </Button>

          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceed(activeStep)}
              sx={{
                backgroundColor: "#00d4ad",
                "&:hover": { backgroundColor: "#00b89c" },
                "&:disabled": { backgroundColor: "#e0e0e0", color: "#999" },
              }}
            >
              Next
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CreateProjectWizard;
